import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { BTN_PRIMARY, INPUT } from "@/lib/styles";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(email, password, name);
      toast.success(isLogin ? "¡Bienvenido de nuevo!" : "¡Cuenta creada!");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Left: brand / image */}
      <div className="hidden lg:flex relative items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1671159593357-ee577a598f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
          }}
        />
        <div className="absolute inset-0 bg-slate-950/60" />
        <div className="relative z-10 px-16">
          <div className="flex items-center gap-3 mb-6">
            <img src="/icon-192.png" alt="Notacora" className="w-12 h-12 rounded-xl" />
            <span className="font-heading font-semibold text-3xl text-white tracking-tight">Notacora</span>
          </div>
          <h2 className="font-heading font-light text-4xl text-white leading-tight mb-4">
            Tus ideas, notas y mapas mentales.
          </h2>
          <p className="font-body text-slate-300 text-lg max-w-md">
            Captura, organiza y visualiza todo. Sincronizado en cada uno de tus dispositivos.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="/icon-192.png" alt="Notacora" className="w-10 h-10 rounded-lg" />
            <span className="font-heading font-semibold text-2xl tracking-tight">Notacora</span>
          </div>

          <h1 className="font-heading font-light text-3xl tracking-tight mb-1">
            {isLogin ? "Iniciar sesión" : "Crear cuenta"}
          </h1>
          <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-8">
            {isLogin ? "Bienvenido de nuevo a Notacora." : "Empieza a organizar tus ideas."}
          </p>

          <form onSubmit={submit} className="flex flex-col gap-4" data-testid="auth-form">
            {!isLogin && (
              <input className={INPUT} placeholder="Tu nombre" value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name-input" />
            )}
            <input className={INPUT} type="email" placeholder="Correo electrónico" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="auth-email-input" />
            <div className="relative">
              <input
                className={`${INPUT} pr-11`}
                type={showPassword ? "text" : "password"}
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {isLogin && (
              <div className="text-right -mt-1">
                <Link to="/recuperar" className="text-xs text-indigo-500 hover:text-indigo-400 font-medium" data-testid="forgot-password-link">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            )}
            {error && (
              <p className="font-body text-xs text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2" data-testid="auth-error">
                {error}
              </p>
            )}
            <button type="submit" className={`${BTN_PRIMARY} w-full py-3`} disabled={loading} data-testid="auth-submit">
              {loading ? "..." : isLogin ? "Entrar" : "Registrarme"}
            </button>
          </form>

          <p className="font-body text-sm mt-6 text-slate-500 dark:text-slate-400">
            {isLogin ? "¿No tienes cuenta? " : "¿Ya tienes cuenta? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-indigo-500 hover:text-indigo-400 font-medium" data-testid="auth-switch-link">
              {isLogin ? "Regístrate" : "Inicia sesión"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
